import React, { useEffect } from "react";
import cn from "classnames";
import styles from "./style.scss";

interface Props {
  list: {
    label: string;
    key: string;
    icon?: React.ReactNode;
  }[];
  onClick: (key: string) => void;
  onOutsideClick?: () => void;
  direction?: "down" | "up";
  onBlur?: () => void;
}

const PulldownMenu: React.FC<Props> = ({
  list,
  onClick,
  onOutsideClick,
  direction = "down",
  onBlur
}) => {
  const containerRef = React.useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!onOutsideClick) return;

    const handleDocumentClick = (event: MouseEvent | TouchEvent) => {
      const containerNode = containerRef.current as Node | null;
      if (!containerNode) return;

      const target = event.target;
      if (
        target &&
        "nodeType" in target &&
        !containerNode.contains(target as Node)
      ) {
        onOutsideClick();
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("touchstart", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("touchstart", handleDocumentClick);
    };
  }, [onOutsideClick]);

  const handleBlur = (event: React.FocusEvent<HTMLUListElement>) => {
    if (!onBlur) return;

    const nextFocusedElement = event.relatedTarget as Node | null;
    const containerNode = containerRef.current as Node | null;
    if (
      !containerNode ||
      !nextFocusedElement ||
      !containerNode.contains(nextFocusedElement)
    ) {
      onBlur();
    }
  };

  return (
    <ul
      className={cn(styles.container, {
        [styles.upward]: direction === "up"
      })}
      ref={containerRef}
      onBlur={handleBlur}
    >
      {list.map(item => (
        <li key={item.key}>
          <button type="button" onClick={() => onClick(item.key)}>
            {item.icon}
            {item.label}
          </button>
        </li>
      ))}
    </ul>
  );
};

export default PulldownMenu;
